import { useCallback, useEffect, useState } from "react";
import { useShallow } from "zustand/react/shallow";
import { CalendarDays } from "lucide-react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Calendar } from "@/components/ui/calendar";
import { useReplayStore } from "@/stores/replayStore";
import { useChartStore } from "@/stores/chartStore";
import { parseTimeframeToMinutes } from "@/lib/lytick/replay-utils";
import { searchTimeNearest } from "@/lib/lytick/columnar";
import { getBaseColumns } from "@/lib/lytick/chart-data-service";
import { useUIStore } from "@/stores/uiStore";

interface ReplayDateSeekSheetProps {
  children: React.ReactNode;
  /** When provided, called with the chosen timestamp instead of seekToTimestamp (e.g., placement mode). */
  onConfirm?: (epochMs: number) => void;
  /** Pre-select this date when the sheet opens (used in placement mode to show the line's current position). */
  initialDate?: number | null;
}

function formatValidationDate(epochMs: number, subDaily: boolean): string {
  if (subDaily) {
    return new Intl.DateTimeFormat(undefined, {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    }).format(epochMs);
  }
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "2-digit",
    year: "numeric",
  }).format(epochMs);
}

export function ReplayDateSeekSheet({ children, onConfirm, initialDate }: ReplayDateSeekSheetProps) {
  const [open, setOpen] = useState(false);

  const { seekToTimestamp, currentCandle } = useReplayStore(
    useShallow((state) => ({
      seekToTimestamp: state.seekToTimestamp,
      currentCandle: state.currentCandle,
    })),
  );

  const { oldestEpoch, latestEpoch } = useChartStore(
    useShallow((s) => ({
      oldestEpoch: s.oldestEpoch,
      latestEpoch: s.latestEpoch,
    })),
  );

  const timeframe = useChartStore((s) => s.timeframe);
  const tfMinutes = parseTimeframeToMinutes(timeframe);
  const showTimeInput = tfMinutes !== null && tfMinutes < 1440;
  const subDaily = tfMinutes !== null && tfMinutes < 1440;

  const [selected, setSelected] = useState<Date | undefined>(undefined);
  const [timeValue, setTimeValue] = useState("00:00");
  const [validationError, setValidationError] = useState<string | null>(null);

  // Pre-fill when the sheet opens. Priority: initialDate (placement line position) → currentCandle (active replay)
  useEffect(() => {
    if (!open) return;
    const seedMs = initialDate ?? currentCandle?.timestamp ?? null;
    if (seedMs != null) {
      const d = new Date(seedMs);
      setSelected(d);
      const hh = String(d.getHours()).padStart(2, "0");
      const mm = String(d.getMinutes()).padStart(2, "0");
      setTimeValue(`${hh}:${mm}`);
    } else {
      setSelected(undefined);
      setTimeValue("00:00");
    }
  }, [open, initialDate, currentCandle]);

  useEffect(() => {
    if (!open) setValidationError(null);
  }, [open]);

  // Use the full chart data bounds (not replay session bounds) so the user can pick any historical date
  const fromDate = oldestEpoch != null ? new Date(oldestEpoch) : undefined;
  const toDate = latestEpoch != null ? new Date(latestEpoch) : undefined;

  const handleSelect = useCallback((date: Date | undefined) => {
    if (!date) return;
    setSelected(date);
    setValidationError(null);
  }, []);

  const handleConfirm = useCallback(() => {
    if (!selected) return;
    const d = new Date(selected.getTime());
    if (showTimeInput) {
      const [hours, minutes] = timeValue.split(":").map(Number);
      d.setHours(hours || 0, minutes || 0, 0, 0);
    } else {
      d.setHours(0, 0, 0, 0);
    }
    const targetMs = d.getTime();

    if (oldestEpoch !== null && latestEpoch !== null) {
      if (targetMs < oldestEpoch) {
        setValidationError(`No data before ${formatValidationDate(oldestEpoch, subDaily)}`);
        return;
      }
      if (targetMs > latestEpoch) {
        setValidationError(`Beyond available data (up to ${formatValidationDate(latestEpoch, subDaily)})`);
        return;
      }
    }

    if (onConfirm) {
      onConfirm(targetMs);
      setValidationError(null);
      setOpen(false);
      return;
    }

    seekToTimestamp(targetMs);

    const base = getBaseColumns();
    if (base && base.length > 0) {
      const nearestIdx = searchTimeNearest(base, targetMs);
      const nearestTs = base.time[nearestIdx];
      if (nearestTs != null && Math.abs(nearestTs - targetMs) > 0) {
        useUIStore.getState().addToast(
          `Jumped to nearest available candle — ${formatValidationDate(nearestTs, subDaily)}`,
          "info",
        );
      }
    }

    setValidationError(null);
    setOpen(false);
  }, [selected, timeValue, showTimeInput, seekToTimestamp, onConfirm, oldestEpoch, latestEpoch, subDaily]);

  const sharedBody = (
    <>
      {/* Calendar */}
      <div className="px-3 py-3">
        <Calendar
          mode="single"
          selected={selected}
          onSelect={handleSelect}
          defaultMonth={selected ?? fromDate}
          disabled={[
            ...(fromDate ? [{ before: fromDate }] : []),
            ...(toDate ? [{ after: toDate }] : []),
          ]}
          className="bg-transparent p-0"
          classNames={{
            caption_label: "text-xs font-semibold text-foreground",
            weekday: "w-9 pb-2 text-center text-xxs font-medium text-muted-foreground",
            day: "w-9 h-9 p-0 text-center align-middle",
            today: "rounded-full text-golden font-semibold",
            selected: "",
            outside: "opacity-20",
            disabled: "opacity-20 pointer-events-none",
          }}
        />
      </div>

      {/* Time input */}
      {showTimeInput && (
        <div
          className="flex items-center gap-3 px-4 py-2.5 border-t"
          style={{ borderColor: "var(--lyt-border-default)" }}
        >
          <span className="text-xxs text-muted-foreground shrink-0 w-8">Time</span>
          <input
            type="time"
            value={timeValue}
            onChange={(e) => setTimeValue(e.target.value)}
            className="h-7 flex-1 rounded-md border px-2 text-xs font-mono-tabular focus:outline-none focus:ring-1 focus:ring-golden/40"
            style={{
              backgroundColor: "var(--bg-card)",
              borderColor: "var(--lyt-border-default)",
              color: "var(--text-primary)",
            }}
          />
        </div>
      )}

      {/* Validation error */}
      {validationError && (
        <div className="px-4 pb-2">
          <p className="text-xs text-loss leading-tight">{validationError}</p>
        </div>
      )}

      {/* Footer */}
      <div
        className="flex items-center justify-between gap-3 px-4 py-3 border-t"
        style={{ borderColor: "var(--lyt-border-default)" }}
      >
        <button
          type="button"
          onClick={() => setOpen(false)}
          className="text-xs text-muted-foreground hover:text-foreground transition-colors"
        >
          Cancel
        </button>
        <button
          type="button"
          disabled={!selected}
          onClick={handleConfirm}
          className="flex items-center justify-center h-7 px-4 rounded-full bg-golden text-background text-xs font-bold transition-transform hover:scale-[1.02] active:scale-[0.98] disabled:opacity-40 disabled:pointer-events-none"
        >
          {onConfirm ? "Confirm" : "Jump"}
        </button>
      </div>
    </>
  );

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>{children}</PopoverTrigger>
      <PopoverContent
        align="start"
        side="bottom"
        sideOffset={8}
        className="w-auto p-0 shadow-[0_8px_40px_rgba(0,0,0,0.55)]"
        style={{
          backgroundColor: "var(--bg-elevated)",
          borderColor: "var(--lyt-border-default)",
          borderRadius: "var(--radius-lg, 12px)",
        }}
      >
        {/* Header */}
        <div
          className="flex items-center gap-2 px-4 pt-3.5 pb-3 border-b"
          style={{ borderColor: "var(--lyt-border-default)" }}
        >
          <CalendarDays className="size-3.5 text-golden shrink-0" />
          <span className="text-xs font-semibold text-foreground">Set date</span>
        </div>

        {sharedBody}
      </PopoverContent>
    </Popover>
  );
}
